import './governanceDialog.scss';
import { Grid, Typography, Button } from '@mui/material';
import { IRoom } from '../../../interfaces/IPartnerPms';
import { GovernanceStatusData, GovernanceStatusEnum, RoomStatusData, RoomStatusEnum } from '../../../data/Enums';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog, { IDefaultDialogProps } from '../../Dialog';
import { ActionButton } from '../../Button';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import FormHelper from '../../../helpers/FormHelper';
import DefaultConstants from '../../../data/Constants';
import { usePartnerPmsContext } from '../../../contexts/PartnerPmsContext';
import { useUserContext } from '../../../contexts/UserContext';
import { DryCleaning, Fastfood, InfoOutlined, PendingActions, WhereToVote } from '@mui/icons-material';
import StringHelper from '../../../helpers/StringHelper';
import useDialog from '../../../hooks/useDialog';
import RoomInformationsDialog from './RoomInformationsDialog';
import DateTimeHelper from '../../../helpers/DateTimeHelper';
import ReservationInformationsDialog from './ReservationInformationsDialog';
import DoNotDisturbConfirmationDialog from './DoNotDisturbConfirmationDialog';
import FormOption from '../../FormOption';
import PartnerPmsService from '../../../services/PartnerPmsService';
import RoomServiceItemsListDialog from './RoomServiceItemsListDialog';
import GovernanceItemsDialog from './GovernanceItemsDialog';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import TextField from '../../TextField';
import AddRoomBlockingDialog from './AddRoomBlockingDialog';
import RoomBlockingInformationsDialog from './RoomBlockingInformationsDialog';


const governanceStatusInspection = GovernanceStatusData[GovernanceStatusEnum.Inspection];

const roomBlocking = RoomStatusData[RoomStatusEnum.RoomBlocking];
const ownerReservation = RoomStatusData[RoomStatusEnum.OwnerReservation];
const vipReservation = RoomStatusData[RoomStatusEnum.VipReservation];
const doNotDisturb = RoomStatusData[RoomStatusEnum.DoNotDisturb];
const expectedCheckIn = RoomStatusData[RoomStatusEnum.ExpectedCheckIn];
const expectedCheckOut = RoomStatusData[RoomStatusEnum.ExpectedCheckOut];

interface IGovernanceDialogProps extends IDefaultDialogProps {
    roomId?: number;
}


const GovernanceDialog: React.FC<IGovernanceDialogProps> = ({
    open,
    handleClose,
    roomId = undefined
}) => {
    const { translate } = useLanguageContext();
    const { showToast } = useNotificationContext();
    const { handleRefreshRooms } = usePartnerPmsContext();
    const { enterprise, userPermissions } = useUserContext();

    const [room, setRoom] = useState<IRoom | undefined>(undefined);
    const [governanceStatusId, setGovernanceStatusId] = useState<string | undefined>(undefined);
    const [observations, setObservations] = useState<string>('');

    const roomInformationsDialog = useDialog();
    const [reservationKey, setReservationKey] = useState<'checkInReservation' | 'expectedReservation' | undefined>(undefined);
    const roomServiceItemsListDialog = useDialog();

    const doNotDisturbConfirmationDialog = useDialog();
    
    const addRoomBlockingDialog = useDialog();
    const roomBlockingInformationsDialog = useDialog();

    const governanceItemsDialog = useDialog();
    const [hasPendingGovernanceItems, setHasPendingGovernanceItems] = useState<boolean>(false);

    useEffect(() => {
        if (roomId) {
            setRoom(undefined);
            handleRefreshSelectedRoom();

            roomInformationsDialog.handleClose();
            roomServiceItemsListDialog.handleClose();
            doNotDisturbConfirmationDialog.handleClose();
            
            addRoomBlockingDialog.handleClose();
            roomBlockingInformationsDialog.handleClose();
            
            governanceItemsDialog.handleClose();
            setHasPendingGovernanceItems(false);

            setGovernanceStatusId(undefined);
            setObservations('');
        }
        // eslint-disable-next-line
    }, [roomId]);


    const handleRefreshSelectedRoom = useCallback(() => {
        handleRefreshRooms((rooms) => {
            setRoom(rooms.find(r => r.id === roomId));
        });
        // eslint-disable-next-line
    }, [roomId]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (room === undefined) {
            return;
        }

        let formData = FormHelper.Create(event);
        formData = FormHelper.Add(formData, 'roomId', room.id);

        PartnerPmsService.UpdateRoomGovernanceStatus(formData).then(() => {
            handleClose();
        });

        // eslint-disable-next-line
    }, [room]);


    // Permissions
    const editGovernanceStatus = useMemo(() => {
        return userPermissions.includes(`Governance.RoomOverview.EditGovernanceStatus`);
    }, [userPermissions]);

    const editRoomObservations = useMemo(() => {
        return userPermissions.includes(`Governance.RoomOverview.EditRoomObservations`);
    }, [userPermissions]);

    const launchRoomServiceItems = useMemo(() => {
        if (enterprise === undefined) {
            return false;
        }
        
        const roomServicePointOfSaleId = enterprise.roomServicePointOfSaleId ?? '';
        if (roomServicePointOfSaleId.trim().length === 0) {
            return false;
        }

        return userPermissions.includes(`Governance.RoomOverview.LaunchRoomServiceItems`);
    }, [enterprise, userPermissions]);

    const editDoNotDisturbStatus = useMemo(() => {
        return userPermissions.includes(`Governance.RoomOverview.EditDoNotDisturbStatus`);
    }, [userPermissions]);

    const addRoomBlocking = useMemo(() => {
        if (room === undefined) {
            return false;
        }
        
        const roomKey = (roomBlocking.partnerId ?? '') as keyof(IRoom);
        return userPermissions.includes(`Governance.RoomOverview.RoomBlocking.Add`) && room[roomKey] !== true;
    }, [userPermissions, room]);

    const governanceItems = useMemo(() => {
        if (enterprise === undefined || room === undefined) {
            return [];
        }

        if (room.governanceStatusId !== governanceStatusInspection.partnerId && (governanceStatusId === undefined || !enterprise.governanceStatusAllowedToDisplayGovernanceItems.includes(governanceStatusId))) {
            return [];
        }
        
        const roomTypeId = room.roomTypeId ?? 0;
        return enterprise.governanceItems.filter(i => i.roomTypes.length === 0 || i.roomTypes.includes(+roomTypeId)) ?? [];
    }, [enterprise, room, governanceStatusId]);


    const contentActionButtons = useMemo(() => {
        const elements = [] as JSX.Element[];

        if (room) {
            // Room informations
            elements.push(<Button 
                className='action-button'  
                onClick={() => roomInformationsDialog.handleOpen()}
                key='roomInformation'
            >
                <InfoOutlined />
                <Typography>{translate('roomInformation')}</Typography>
            </Button>);
            
            // Governance items
            if (governanceItems.length > 0) {
                const actionButtonProps = {
                    Icon: DryCleaning,
                    name: 'governanceItems'
                };

                if (room.governanceStatusId === governanceStatusInspection.partnerId) {
                    actionButtonProps.Icon = PendingActions;
                    actionButtonProps.name = 'housekeepingDetails';
                }
                
                elements.push(<Button 
                    className='action-button'  
                    onClick={() => governanceItemsDialog.handleOpen()}
                    key='governanceItems'
                >
                    <actionButtonProps.Icon />
                    <Typography>{translate(actionButtonProps.name)}</Typography>
                </Button>);
            }
            
            // If exists checkin reservation
            if (room.checkInReservation) {
                const checkInReservation = room.checkInReservation; 
    
                // Vip guest
                if (checkInReservation.ownerGuestType) {
                    elements.push(<Button 
                        className='action-button' 
                        sx={{ color: `${ownerReservation.color} !important` }} 
                        onClick={() => setReservationKey('checkInReservation')}
                        key={ownerReservation.key}
                    >
                        <ownerReservation.Icon />
                        <Typography>{translate(ownerReservation.name)}</Typography>
                    </Button>);
                }
                
                // Vip guest
                else if (checkInReservation.vipGuestType) {
                    elements.push(<Button 
                        className='action-button' 
                        sx={{ color: `${vipReservation.color} !important` }} 
                        onClick={() => setReservationKey('checkInReservation')}
                        key={vipReservation.key}
                    >
                        <vipReservation.Icon />
                        <Typography>{translate(vipReservation.name)}</Typography>
                    </Button>);
                } 
                
                // Expected checkout
                else if (DateTimeHelper.Equals(checkInReservation.checkOutDate ?? '', DateTimeHelper.Now())) {
                    elements.push(<Button 
                        className='action-button' 
                        sx={{ color: `${expectedCheckOut.color} !important` }} 
                        onClick={() => setReservationKey('checkInReservation')}
                        key={expectedCheckOut.key}
                    >
                        <expectedCheckOut.Icon />
                        <Typography>{translate(expectedCheckOut.name)}</Typography>
                    </Button>);
                } 
                
                // Default
                else {
                    elements.push(<Button 
                        className='action-button' 
                        onClick={() => setReservationKey('checkInReservation')}
                        key='default'
                    >
                        <WhereToVote />
                        <Typography>{translate('checkInReservation')}</Typography>
                    </Button>);
                }

                // Launch room service items
                if (launchRoomServiceItems) {
                    elements.push(<Button 
                        className='action-button' 
                        onClick={() => roomServiceItemsListDialog.handleOpen()}
                        key='launchItems'
                    >
                        <Fastfood />
                        <Typography>{translate('launchItems')}</Typography>
                    </Button>);
                }
            }
    
            // Expected checkin
            if (room.expectedCheckIn && room.expectedReservation) {
                elements.push(<Button 
                    className='action-button' 
                    sx={{ color: `${expectedCheckIn.color} !important` }} 
                    onClick={() => setReservationKey('expectedReservation')}
                    key={expectedCheckIn.key}
                >
                    <expectedCheckIn.Icon />
                    <Typography>{translate(expectedCheckIn.name)}</Typography>
                </Button>);
            }
    
            // Do not disturb
            if (editDoNotDisturbStatus) {
                let doNotDisturbPrefixTitle = 'enable';
                let doNotDisturbColor = '#bdbdbd';
        
                if (room.doNotDisturb) {
                    doNotDisturbPrefixTitle = 'disable';
                    doNotDisturbColor = doNotDisturb.color;
                }
                
                elements.push(<Button 
                    className='action-button' 
                    sx={{ color: `${doNotDisturbColor} !important` }}
                    onClick={() => doNotDisturbConfirmationDialog.handleOpen()}
                    key={doNotDisturb.key}
                >
                    <doNotDisturb.Icon />
                    <Typography>{`${translate(doNotDisturbPrefixTitle)} "${translate(doNotDisturb.name).toLocaleLowerCase()}"`}</Typography>
                </Button>);
            }


            // Room blocking informations
            const roomBlockingKey = (roomBlocking.partnerId ?? '') as keyof(IRoom);
            if (room[roomBlockingKey] === true) {
                elements.push(<Button 
                    className='action-button'  
                    sx={{ color: `${roomBlocking.color} !important` }}
                    onClick={() => roomBlockingInformationsDialog.handleOpen()}
                    key='roomBlockingInformation'
                >
                    <roomBlocking.Icon />
                    <Typography>{translate('blockingInformation')}</Typography>
                </Button>);
            } 
            
            // Add room blocking
            else if (addRoomBlocking) {
                elements.push(<Button 
                    className='action-button'  
                    sx={{ color: `${roomBlocking.color} !important` }}
                    onClick={() => addRoomBlockingDialog.handleOpen()}
                    key={roomBlocking.key}
                >
                    <roomBlocking.Icon />
                    <Typography>{translate('blockRoom')}</Typography>
                </Button>);
            }
        }

        return elements;
        // eslint-disable-next-line
    }, [room, enterprise, launchRoomServiceItems, addRoomBlocking, governanceItems, translate]);


    const dialogActions = useMemo(() => {
        const elements = [] as JSX.Element[];
        elements.push(<ActionButton action='cancel' onClick={handleClose} />);

        if (editGovernanceStatus) {
            const governanceItemsBlockSubmit = governanceItems.length > 0 && hasPendingGovernanceItems;
            elements.push(<ActionButton 
                action='save' 
                type={governanceItemsBlockSubmit ? 'button' : 'submit'} 
                onClick={() => {
                    if (governanceItemsBlockSubmit) {
                        governanceItemsDialog.handleOpen();
                        showToast('warning', translate('_governanceDialogGovernanceItemsPendingMarking'));
                    }
                }} 
            />);
        }

        return elements;
        // eslint-disable-next-line
    }, [translate, editGovernanceStatus, governanceItems, hasPendingGovernanceItems]);


    if (room === undefined) {
        return null;
    }
    
    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={`${translate('room')} ${room.description}`}
            dialogActions={dialogActions}
            fullWidth={true}
            fullHeight={true}
            maxWidth='sm'
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween overflow-inherit governancedialog-container'
            afterForm={room && <>
                <RoomInformationsDialog
                    open={roomInformationsDialog.open}
                    handleClose={() => roomInformationsDialog.handleClose()}
                    room={room}
                    handleRefreshSelectedRoom={() => handleRefreshSelectedRoom()}
                    editRoomObservations={editRoomObservations}
                />
    
                {
                    (room.checkInReservation || room.expectedReservation) && (
                        <ReservationInformationsDialog 
                            open={reservationKey !== undefined}
                            handleClose={() => setReservationKey(undefined)}
                            reservation={reservationKey ? room[reservationKey] : undefined}
                            isCheckIn={reservationKey === 'checkInReservation'}
                            handleRefreshSelectedRoom={() => handleRefreshSelectedRoom()}
                        />
                    )
                }

                {
                    launchRoomServiceItems && room.checkInReservation && (
                        <RoomServiceItemsListDialog 
                            open={roomServiceItemsListDialog.open}
                            handleClose={() => roomServiceItemsListDialog.handleClose()}
                            room={room}
                            reservation={room.checkInReservation}
                        />
                    )
                }
    
                {
                    editDoNotDisturbStatus && (
                        <DoNotDisturbConfirmationDialog 
                            open={doNotDisturbConfirmationDialog.open}
                            handleClose={() => doNotDisturbConfirmationDialog.handleClose()}
                            room={room}
                            handleRefreshSelectedRoom={() => handleRefreshSelectedRoom()}
                        />
                    )
                }
    
                {
                    addRoomBlocking && (
                        <AddRoomBlockingDialog 
                            open={addRoomBlockingDialog.open}
                            handleClose={() => addRoomBlockingDialog.handleClose()}
                            room={room}
                            handleRefreshSelectedRoom={() => handleRefreshSelectedRoom()}
                        />
                    )
                }

                {
                    roomBlocking.partnerId && room[roomBlocking.partnerId as keyof(IRoom)] === true && (
                        <RoomBlockingInformationsDialog 
                            open={roomBlockingInformationsDialog.open}
                            handleClose={() => roomBlockingInformationsDialog.handleClose()}
                            room={room}
                            handleRefreshSelectedRoom={() => handleRefreshSelectedRoom()}
                        />
                    )
                }
            </>}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing}>
                {
                    contentActionButtons.length > 0 && <Grid container className='actions-container' marginTop={3}>
                        {contentActionButtons}
                    </Grid>
                }

                {
                    editGovernanceStatus && <Grid item xs={12} marginTop={4}>
                        <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                            <Grid item xs={12}>
                                <Typography variant='subtitle2' className='fontweight-600'>{translate('_changeGovernanceStatusTo')}</Typography>
                            </Grid>
                
                            {
                                Object.values(GovernanceStatusData)
                                    .filter(s => s.partnerId && (
                                        s.partnerId === room.governanceStatusId ||
                                        userPermissions.includes(`Governance.RoomOverview.EditGovernanceStatus.${StringHelper.FirstLetterUpperCase(s.name)}`)
                                    ))
                                    .map(s => {
                                        const currentRoomGovernanceStatus = s.partnerId === room.governanceStatusId;
                                        const disabled = currentRoomGovernanceStatus;
                
                                        return <Grid item sm={4} xs={6} key={s.key}>
                                            <FormOption
                                                name='governanceStatusId'
                                                type='radio'
                                                value={s.partnerId ?? ''}
                                                title={translate(s.name)}
                                                selected={currentRoomGovernanceStatus || s.partnerId === governanceStatusId}
                                                disabled={disabled}
                                                required={governanceStatusId === undefined}
                                                onClick={() => setGovernanceStatusId(s.partnerId ?? '')}
                                                Icon={s.Icon}
                                                orientation='vertical'
                                                selectedProps={{
                                                    sx: {
                                                        backgroundColor: `${s.color} !important`
                                                    }
                                                }}
                                            />
                                        </Grid>;
                                    })
                            }
                        </Grid>

                        <Grid item xs={12} marginTop={2}>
                            <TextField 
                                label={translate('observations')} 
                                name='observations' 
                                value={observations} 
                                required={false}
                                onChange={e => setObservations(e.target.value)}
                                multiline={true}
                                minRows={4}
                                maxRows={4}
                            />
                        </Grid>
                    </Grid>
                }

                {
                    governanceItems.length > 0 && (
                        <GovernanceItemsDialog
                            open={governanceItemsDialog.open}
                            handleClose={() => governanceItemsDialog.handleClose()}
                            governanceItems={governanceItems}
                            room={room}
                            hasPendingGovernanceItems={(value) => setHasPendingGovernanceItems(value)}
                        />
                    )
                }
            </Grid>
        </Dialog>
    );
}

export default GovernanceDialog;